export interface SampledGoalImpactAudio {
  readonly url: string;
  readonly gain: number;
}

export type GoalExplosionAudioRouting = 'global' | 'spatial';

export interface SampledGoalAmbienceAudio {
  readonly url: string;
  readonly gain: number;
  readonly loop: boolean;
  readonly fadeOutSeconds: number;
}

export type GoalExplosionAnimationAudioPreviousBehavior = 'continue' | 'stop';

export interface GoalExplosionAnimationAudioCue {
  readonly animationLayerId: string;
  readonly url: string;
  readonly gain: number;
  readonly delaySeconds: number;
  readonly startSeconds: number;
  readonly previousBehavior: GoalExplosionAnimationAudioPreviousBehavior;
}

export interface GoalExplosionAudioDefinition {
  readonly routing: GoalExplosionAudioRouting;
  readonly impact: SampledGoalImpactAudio | null;
  readonly ambience: SampledGoalAmbienceAudio | null;
  readonly animationCues: readonly GoalExplosionAnimationAudioCue[];
}

export function validateGoalExplosionAudioDefinition(definition: GoalExplosionAudioDefinition): void {
  if (definition.routing !== 'global' && definition.routing !== 'spatial') {
    throw new Error('Goal explosion audio routing must be global or spatial.');
  }

  if (definition.impact) {
    if (!definition.impact.url.trim()) throw new Error('Goal explosion impact sample URL must not be empty.');
    nonNegative(definition.impact.gain, 'Goal explosion impact gain');
  }

  if (definition.ambience) {
    if (!definition.ambience.url.trim()) throw new Error('Goal explosion ambience sample URL must not be empty.');
    unit(definition.ambience.gain, 'Goal explosion ambience gain');
    if (typeof definition.ambience.loop !== 'boolean') throw new Error('Goal explosion ambience loop must be boolean.');
    nonNegative(definition.ambience.fadeOutSeconds, 'Goal explosion ambience fade-out duration');
  }

  if (!Array.isArray(definition.animationCues) || definition.animationCues.length > 16) {
    throw new Error('Goal explosion audio supports at most 16 animation cues.');
  }
  const animationLayerIds = new Set<string>();
  for (const cue of definition.animationCues) {
    if (!cue.animationLayerId.trim() || cue.animationLayerId.length > 64 || animationLayerIds.has(cue.animationLayerId)) {
      throw new Error('Goal explosion animation audio cue layer IDs must be unique, non-empty, and at most 64 characters.');
    }
    animationLayerIds.add(cue.animationLayerId);
    if (!cue.url.trim()) throw new Error(`Goal explosion animation cue ${cue.animationLayerId} sample URL must not be empty.`);
    unit(cue.gain, `Goal explosion animation cue ${cue.animationLayerId} gain`);
    nonNegative(cue.delaySeconds, `Goal explosion animation cue ${cue.animationLayerId} delay`);
    nonNegative(cue.startSeconds, `Goal explosion animation cue ${cue.animationLayerId} start time`);
    if (cue.previousBehavior !== 'continue' && cue.previousBehavior !== 'stop') {
      throw new Error(`Goal explosion animation cue ${cue.animationLayerId} previous behavior is invalid.`);
    }
  }
}

function unit(value: number, label: string): void {
  if (!Number.isFinite(value) || value < 0 || value > 1) throw new Error(`${label} must be between 0 and 1.`);
}

function nonNegative(value: number, label: string): void {
  if (!Number.isFinite(value) || value < 0) throw new Error(`${label} must be a non-negative finite number.`);
}
